/**
 * Paging and budget constants shared by the interface and the sync worker.
 *
 * They live in their own module so that a screen can import a page size without
 * importing the worker — and therefore without pulling the transport, the
 * scheduler and the protobuf runtime into the main bundle for the sake of one
 * number.
 */

/** Section 14: load 50 latest messages, then keyset-load the previous 50. */
export const HISTORY_PAGE_SIZE = 50;

/** Section 05: target 16–64 KiB pages on weak networks. */
export const SYNC_PAGE_BYTES = 64 * 1024;

/**
 * CR-01 §8: 16 KiB sync pages in weak mode.
 *
 * A soft content budget, not permission to exceed the envelope maximum. The
 * existing overhead reserve and the single-oversized-item exception still apply:
 * a page small enough to arrive on a bad link is the point, and a message larger
 * than the budget is still delivered rather than becoming unreachable.
 */
export const SYNC_PAGE_BYTES_WEAK = 16 * 1024;
export const HISTORY_PAGE_BYTES = 64 * 1024;

/** Section 14: independent history queries may run concurrently, at most four. */
export const MAX_CONCURRENT_QUERIES = 4;

/** Section 14: at most four mutating requests in flight across the account. */
export const MAX_CONCURRENT_MUTATIONS = 4;

/** Section 13: mutation retry is min(60 s, 500 ms x 2^attempt) with full jitter. */
export const RETRY_BASE_MS = 500;
export const RETRY_CAP_MS = 60_000;

// ---------------------------------------------------------------------------
// Section 06 media limits
// ---------------------------------------------------------------------------

/**
 * Section 06: S3 multipart uploads with 8 MiB parts, except the final part.
 *
 * The value is also the S3 minimum for a nonfinal part, which is why it cannot
 * be lowered to make progress reporting finer-grained: a smaller part would be
 * rejected by the object store rather than merely being inefficient.
 */
export const UPLOAD_PART_BYTES = 8 * 1024 * 1024;

/** Section 06: two concurrent parts on a normal network, one on a weak one. */
export const UPLOAD_PARTS_NORMAL = 2;
export const UPLOAD_PARTS_WEAK = 1;

/** Section 06: request deadlines are 180 s normal and 600 s weak. */
export const TRANSFER_DEADLINE_NORMAL_MS = 180_000;
export const TRANSFER_DEADLINE_WEAK_MS = 600_000;

/** Section 06: 4 GiB per object. */
export const MAX_FILE_BYTES = 4 * 1024 ** 3;

/** Section 06: 16,384 UTF-8 bytes of text, 100 entities, 4,096-byte captions. */
export const MAX_TEXT_BYTES = 16_384;
export const MAX_ENTITY_COUNT = 100;
export const MAX_CAPTION_BYTES = 4_096;

/** Section 06: an album carries 2–10 objects in one message. */
export const ALBUM_MIN = 2;
export const ALBUM_MAX = 10;

/** Section 06: voice notes are capped at 60 minutes, video notes at 60 seconds. */
export const MAX_VOICE_MS = 60 * 60 * 1000;
export const MAX_VIDEO_NOTE_MS = 60 * 1000;

/** Section 14: confirm before downloading a file above 20 MiB on a metered link. */
export const METERED_DOWNLOAD_CONFIRM_BYTES = 20 * 1024 * 1024;

/** Section 14: the waveform is 100 normalized samples. */
export const WAVEFORM_SAMPLES = 100;

/** Section 06: voice playback runs at 1x, 1.5x or 2x and nothing else. */
export const PLAYBACK_RATES = [1, 1.5, 2] as const;
export type PlaybackRate = (typeof PLAYBACK_RATES)[number];

/** Section 06: playback position updates are debounced by five seconds. */
export const PLAYBACK_POSITION_DEBOUNCE_MS = 5_000;

// ---------------------------------------------------------------------------
// Section 06 messaging limits
// ---------------------------------------------------------------------------

/** Section 06: the six default reactions, in their fixed order. */
export const DEFAULT_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '👎'] as const;

/** Section 06: up to three distinct reactions per user per message. */
export const MAX_REACTIONS_PER_USER = 3;

/** Section 06: a conversation holds at most 100 pins. */
export const MAX_PINS = 100;

/** Section 06: drafts synchronize after one second of idle time. */
export const DRAFT_SYNC_IDLE_MS = 1_000;

/** Section 06: search queries are at most 256 bytes and return at most 50 hits. */
export const MAX_SEARCH_QUERY_BYTES = 256;
export const SEARCH_PAGE_SIZE = 50;

/** Section 06: a poll has 2–10 options; question ≤300 and option ≤100 scalars. */
export const POLL_MIN_OPTIONS = 2;
export const POLL_MAX_OPTIONS = 10;
export const POLL_MAX_QUESTION_SCALARS = 300;
export const POLL_MAX_OPTION_SCALARS = 100;
export const POLL_MAX_EXPLANATION_BYTES = 1_024;

/** Section 06: slow mode is one of exactly these four values. */
export const SLOW_MODE_CHOICES = [0, 10, 30, 60] as const;

/** Section 03/06: a member page and a topic page. */
export const MEMBER_PAGE_SIZE = 50;
export const TOPIC_PAGE_SIZE = 50;
